import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { DownloadSimpleIcon } from "@phosphor-icons/react";
import { api, apiError, PUBLIC_HOST, SITE_ORIGIN } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PlusIcon, BroadcastIcon, TagIcon, CurrencyDollarIcon, SealCheckIcon, TimerIcon, TrophyIcon, WarningIcon, CopyIcon, HandHeartIcon, ShareNetworkIcon, CrownSimpleIcon } from "@phosphor-icons/react";
import TrustTierBadge from "@/components/TrustTierBadge";
import CollectorNudgeBanner from "@/components/CollectorNudgeBanner";
import FounderWalletCard from "@/components/FounderWalletCard";
import MessageTopBuyersModal from "@/components/MessageTopBuyersModal";
import OwnerListingActions from "@/components/OwnerListingActions";
import ComposePostModal from "@/components/ComposePostModal";
import PostCard from "@/components/PostCard";
import FounderCountdownBanner from "@/components/FounderCountdownBanner";

function BidTray({ user }) {
  const [data, setData] = useState({ items: [], winning: 0, outbid: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const fetchTray = async () => {
      try {
        const r = await api.get("/me/active-bids");
        if (alive) setData(r.data);
      } catch {
        /* silent */
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchTray();
    const iv = setInterval(fetchTray, 15000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [user]);

  if (loading) return null;
  const activeItems = data.items.filter((i) => i.status === "active");
  if (!activeItems.length) return null;

  return (
    <section className="mt-10" data-testid="bid-tray">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="overline text-zinc-500">LIVE BID TRAY</p>
          <h3 className="font-display font-black text-2xl tracking-tight mt-1">
            {data.winning > 0 && (
              <span className="text-emerald-600">Winning {data.winning}</span>
            )}
            {data.winning > 0 && data.outbid > 0 && (
              <span className="text-zinc-400 mx-2">·</span>
            )}
            {data.outbid > 0 && (
              <span className="text-red-600">Outbid on {data.outbid}</span>
            )}
            {data.winning === 0 && data.outbid === 0 && (
              <span>Your auctions</span>
            )}
          </h3>
        </div>
        <span className="text-[11px] font-mono tracking-wider text-zinc-400">
          AUTO-REFRESH · 15s
        </span>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {activeItems.map((b) => {
          const winning = b.state === "winning";
          return (
            <Link
              key={b.listing_id}
              to={`/listing/${b.listing_id}`}
              className={`rounded-2xl border-2 p-4 bg-white transition-all hover:-translate-y-0.5 ${
                winning
                  ? "border-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.08)]"
                  : "border-red-400 shadow-[0_0_0_3px_rgba(239,68,68,0.08)]"
              }`}
              data-testid={`bid-tray-item-${b.listing_id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                {winning ? (
                  <span className="pill bg-emerald-500 text-white flex items-center gap-1">
                    <TrophyIcon size={11} weight="fill" /> WINNING
                  </span>
                ) : (
                  <span className="pill bg-red-500 text-white flex items-center gap-1">
                    <WarningIcon size={11} weight="fill" /> OUTBID
                  </span>
                )}
                <span className="text-[10px] font-mono tracking-widest text-zinc-400 uppercase">
                  {b.category}
                </span>
              </div>
              <p className="font-display font-bold text-base leading-tight line-clamp-2">
                {b.title}
              </p>
              <div className="mt-3 flex items-baseline justify-between">
                <div>
                  <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">
                    Current
                  </p>
                  <p className="font-mono font-bold text-[#1D78AB] text-lg">
                    ${Number(b.current_bid || 0).toFixed(2)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">
                    Your bid
                  </p>
                  <p className="font-mono font-semibold text-sm">
                    ${Number(b.my_max_bid || 0).toFixed(2)}
                  </p>
                </div>
              </div>
              {!winning && (
                <p className="text-xs text-red-600 mt-2 font-semibold">
                  Leading bidder: {b.highest_bidder_name || "someone else"} — tap to re-bid
                </p>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function ReferralCard() {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let alive = true;
    api
      .get("/me/referral")
      .then((r) => alive && setState({ loading: false, data: r.data }))
      .catch(() => alive && setState({ loading: false, data: null }));
    return () => {
      alive = false;
    };
  }, []);

  if (state.loading || !state.data?.eligible) return null;
  const { code, signups, paid_referrals, earnings_usd, share_pct, brand_name } = state.data;
  const link = `${PUBLIC_HOST}/register?ref=${code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Referral link copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join Bidorah via ${brand_name}`,
          text: `I'm hosting live auctions on Bidorah — come bid with my invite`,
          url: link,
        });
        return;
      } catch {
        /* user cancelled */
      }
    }
    copy();
  };

  return (
    <section
      className="mt-10 rounded-3xl border-2 border-[#1D78AB] bg-gradient-to-br from-[#1D78AB]/5 via-white to-amber-100/30 p-6 lg:p-8"
      data-testid="referral-card"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="pill bg-[#1D78AB] text-white flex items-center gap-1">
          <HandHeartIcon size={11} weight="fill" /> FOUNDER SELLER REFERRALS
        </span>
        <span className="text-[10px] font-mono tracking-widest text-zinc-400 uppercase">
          {brand_name}
        </span>
      </div>
      <h3 className="font-display font-black text-3xl tracking-tight mt-3">
        Your invite link is live.
      </h3>
      <p className="text-sm text-zinc-600 mt-2 max-w-xl">
        Share it with collectors, buyers, or other sellers. Every signup is attributed to
        you — and you earn <b>{share_pct ?? 2}%</b> of each referred buyer's first paid order,
        released instantly to your payout ledger.
      </p>

      <div className="mt-5 grid sm:grid-cols-[1fr_auto_auto] gap-2 items-stretch">
        <div className="bg-white border border-zinc-200 rounded-xl px-4 py-3 font-mono text-sm truncate flex items-center">
          <span className="truncate" data-testid="referral-link-display">{link}</span>
        </div>
        <Button
          onClick={copy}
          variant="outline"
          className="gap-2 h-auto rounded-xl"
          data-testid="referral-copy-btn"
        >
          <CopyIcon size={16} /> Copy
        </Button>
        <Button
          onClick={share}
          className="gap-2 h-auto rounded-xl bg-[#1D78AB] hover:bg-[#165d85] text-white"
          data-testid="referral-share-btn"
        >
          <ShareNetworkIcon size={16} /> Share
        </Button>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl bg-white border border-zinc-200 p-4">
          <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">CODE</p>
          <p className="font-mono font-bold text-[#1D78AB] text-lg mt-1" data-testid="referral-code">
            {code}
          </p>
        </div>
        <div className="rounded-xl bg-white border border-zinc-200 p-4">
          <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">SIGNUPS</p>
          <p className="font-display font-black text-2xl mt-1">{signups}</p>
        </div>
        <div className="rounded-xl bg-white border border-zinc-200 p-4">
          <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">PAID</p>
          <p className="font-display font-black text-2xl mt-1 text-emerald-600">
            {paid_referrals}
          </p>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-amber-300 to-amber-500 border border-amber-400 p-4 text-zinc-900">
          <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-900/80">
            EARNED
          </p>
          <p
            className="font-display font-black text-2xl mt-1 tracking-tight"
            data-testid="referral-earnings"
          >
            ${Number(earnings_usd || 0).toFixed(2)}
          </p>
        </div>
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Allow deep-linking to a specific tab via `?tab=library` etc. so
  // navbar shortcuts (e.g., "My downloads") can land users directly on
  // the right slice of the dashboard. The Tabs are CONTROLLED so a
  // same-page nav (already on /dashboard, then "My downloads") flips
  // the tab — `defaultValue` only runs on first mount and would silently
  // ignore subsequent URL changes.
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [myListings, setMyListings] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [streamForm, setStreamForm] = useState({ title: "", description: "", category: "Sneakers", scheduled_at: "", visibility: "public", age_restricted: false });
  const [categories, setCategories] = useState([]);
  const [creatingStream, setCreatingStream] = useState(false);
  const [topBuyersOpen, setTopBuyersOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        const [cats, ord, bid] = await Promise.all([
          api.get("/categories"),
          api.get("/me/orders"),
          api.get("/me/bids"),
        ]);
        setCategories(cats.data.categories);
        setMyOrders(ord.data.items);
        setMyBids(bid.data.items);
        if (user.account_type.endsWith("_seller")) {
          const r = await api.get("/listings", { params: { seller_id: user.user_id, status: "" } });
          setMyListings(r.data.items);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [user, navigate]);

  const startStream = async () => {
    setCreatingStream(true);
    try {
      const payload = { ...streamForm };
      if (!payload.scheduled_at) delete payload.scheduled_at;
      else payload.scheduled_at = new Date(payload.scheduled_at).toISOString();
      const { data } = await api.post("/streams", payload);
      const isPrivate = payload.visibility === "private";
      if (data.status === "scheduled") {
        toast.success(
          isPrivate
            ? "Private show scheduled. Share the link manually — it won't appear in feeds."
            : "Stream scheduled. Bookmarkers will be notified when you go live."
        );
        navigate("/dashboard");
      } else {
        toast.success(isPrivate ? "You're live (private). Share the link to invite viewers." : "You're live!");
        navigate(`/stream/${data.id}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreatingStream(false);
    }
  };

  // Controlled active tab. Initialises from the URL on first render,
  // then re-syncs whenever the URL `?tab=` value changes (e.g. user
  // taps "My downloads" while already on /dashboard).
  // NB: declared BEFORE any early returns so the hooks list stays
  // stable across renders.
  const [activeTab, setActiveTab] = useState(
    tabFromUrl ||
      (user?.account_type?.endsWith("_seller") ? "seller" : "orders")
  );
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
    // When the user lands via a navbar shortcut (?tab=library) we
    // scroll to the tab row so the content is visible without
    // requiring them to scroll past the wallet/welcome hero. We do
    // this on every URL-driven tab change, not just the initial mount.
    if (tabFromUrl && typeof document !== "undefined") {
      const el = document.querySelector('[data-testid="dashboard-tabs"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl]);

  if (!user) return null;
  const isSeller = user.account_type.endsWith("_seller");

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="dashboard-page">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline text-zinc-500">YOUR DASHBOARD</p>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-2">
              Welcome, {user.name.split(" ")[0]}
            </h1>
            <div className="mt-2 flex items-center gap-2 text-sm flex-wrap">
              <span
                className={`px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase ${
                  user.segment === "wholesale" ? "bg-amber-500 text-white" : "bg-[#1D78AB] text-white"
                }`}
              >
                {user.segment}
              </span>
              <span className="text-zinc-600">{user.account_type.replace("_", " ")}</span>
              {user.account_type.endsWith("_seller") && user.trust_tier && (
                <TrustTierBadge tier={user.trust_tier} size="sm" />
              )}
              {user.verification_status === "approved" ? (
                <span className="flex items-center gap-1 text-emerald-600 text-xs">
                  <SealCheckIcon size={14} weight="fill" /> verified
                </span>
              ) : (
                <Link to="/verify" className="text-[#1D78AB] text-xs font-semibold">
                  Verify ID →
                </Link>
              )}
            </div>
          </div>
          {isSeller && user.verification_status === "approved" && (
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-50"
                onClick={() => setTopBuyersOpen(true)}
                data-testid="open-top-buyers-modal"
              >
                <CrownSimpleIcon size={16} weight="fill" className="text-amber-500" />
                <span className="hidden sm:inline">Message top buyers</span>
                <span className="sm:hidden">Top buyers</span>
              </Button>
              <Link to="/seller/shipping" data-testid="nav-shipping-link">
                <Button variant="outline" className="gap-2">
                  Shipping
                </Button>
              </Link>
              <Link to="/payouts" data-testid="nav-payouts-link">
                <Button variant="outline" className="gap-2">
                  <PlusIcon size={16} className="hidden" />
                  Payouts
                </Button>
              </Link>
              <Link to="/sell/new" data-testid="new-listing-link">
                <Button className="bg-[#1D78AB] hover:bg-[#165d85] text-white gap-2">
                  <PlusIcon size={16} /> New listing
                </Button>
              </Link>
            </div>
          )}
        </div>

        <CollectorNudgeBanner />

        <FounderWalletCard />

        {user.account_type === "wholesale_buyer" && user.wholesale_buyer_status !== "approved" && (
          <Link
            to="/wholesale-credentials"
            className="mt-6 block rounded-2xl border-2 border-amber-400 bg-amber-50 p-5 hover:bg-amber-100 transition-colors"
            data-testid="dashboard-wholesale-gate-banner"
          >
            <div className="flex items-start gap-3">
              <span className="pill bg-amber-500 text-white flex-shrink-0 mt-1">ACTION REQUIRED</span>
              <div>
                <p className="font-display font-bold text-amber-900">
                  {user.wholesale_buyer_status === "pending"
                    ? "Wholesale credentials under review"
                    : user.wholesale_buyer_status === "rejected"
                    ? "Your wholesale credentials need attention"
                    : "Submit wholesale credentials to unlock buying"}
                </p>
                <p className="text-sm text-amber-900/80 mt-1">
                  {user.wholesale_buyer_status === "pending"
                    ? "Wholesale bidding and checkout are blocked until approval. Usually within 24h."
                    : user.wholesale_buyer_status === "rejected"
                    ? user.wholesale_rejection_reason ||
                      "Click to re-submit with correct reseller permit + TIN + business details."
                    : "You must submit a reseller's permit, TIN, and registered business entity before bidding or buying wholesale. No exceptions."}
                </p>
              </div>
            </div>
          </Link>
        )}

        <div className="mt-8 space-y-4">
        </div>

        <BidTray user={user} />
        {isSeller && <FounderCountdownBanner />}
        {isSeller && <ReferralCard />}

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="mt-10"
          data-testid="dashboard-tabs"
        >
          <TabsList className="bg-zinc-100 border border-zinc-200">
            {isSeller && <TabsTrigger value="seller" data-testid="tab-seller">My listings</TabsTrigger>}
            {isSeller && <TabsTrigger value="streams" data-testid="tab-streams">Go live</TabsTrigger>}
            {isSeller && <TabsTrigger value="posts" data-testid="tab-posts">Posts</TabsTrigger>}
            {isSeller && <TabsTrigger value="highlights" data-testid="tab-highlights">Highlights</TabsTrigger>}
            <TabsTrigger value="bids" data-testid="tab-bids">My bids</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="library" data-testid="tab-library">Library</TabsTrigger>
          </TabsList>

          {isSeller && (
            <TabsContent value="seller" className="mt-6">
              {myListings.length === 0 ? (
                <Empty title="No listings yet" cta="Create your first listing" to="/sell/new" />
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myListings.map((l) => (
                    <div
                      key={l.id}
                      className="border border-zinc-200 p-4 bg-white hover-lift flex flex-col"
                      data-testid={`my-listing-${l.id}`}
                    >
                      <Link
                        to={`/listing/${l.id}`}
                        className="block"
                        data-testid={`my-listing-link-${l.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase ${
                            l.type === "auction" ? "bg-zinc-900 text-white" : "bg-[#1D78AB] text-white"
                          }`}>
                            {l.type === "auction" ? "AUCTION" : "BUY NOW"}
                          </span>
                          <span className="text-xs text-zinc-500">{l.status}</span>
                        </div>
                        <div className="font-display font-bold text-lg line-clamp-1">{l.title}</div>
                        <div className="font-mono text-[#1D78AB] font-bold mt-1">
                          ${(l.current_bid || l.price).toFixed(2)}
                        </div>
                        {l.type === "auction" && (
                          <div className="text-xs text-zinc-500 mt-1">
                            {l.bid_count} bids
                          </div>
                        )}
                      </Link>
                      <div className="mt-3 pt-3 border-t border-zinc-100">
                        <OwnerListingActions
                          listing={l}
                          onChange={(fresh) =>
                            setMyListings((prev) =>
                              prev.map((x) => (x.id === fresh.id ? fresh : x))
                            )
                          }
                          onDeleted={() =>
                            setMyListings((prev) => prev.filter((x) => x.id !== l.id))
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {isSeller && (
            <TabsContent value="streams" className="mt-6">
              <div className="border border-zinc-200 p-6 max-w-xl bg-white">
                <div className="overline text-zinc-500 mb-2">Launch a live stream</div>
                <h3 className="font-display font-black text-2xl">Go live in seconds</h3>
                <p className="text-sm text-zinc-600 mt-1">
                  Create a stream room and add auction lots. Buyers join, chat, and bid in real time.
                </p>
                <div className="mt-4 space-y-3">
                  {/* Public / Private show segmented tab — sellers pick the
                      audience model. PUBLIC = appears on Live grid + category
                      alerts. PRIVATE = unlisted, link-only. */}
                  <div className="rounded-xl bg-zinc-100 p-1 grid grid-cols-2 gap-1" data-testid="stream-visibility-tabs">
                    <button
                      type="button"
                      onClick={() => setStreamForm({ ...streamForm, visibility: "public" })}
                      className={`h-9 rounded-lg text-xs font-bold tracking-tight transition ${
                        streamForm.visibility === "public"
                          ? "bg-white text-zinc-900 shadow-sm"
                          : "text-zinc-600 hover:text-zinc-800"
                      }`}
                      data-testid="stream-visibility-public"
                    >
                      🌐 Public show
                    </button>
                    <button
                      type="button"
                      onClick={() => setStreamForm({ ...streamForm, visibility: "private" })}
                      className={`h-9 rounded-lg text-xs font-bold tracking-tight transition ${
                        streamForm.visibility === "private"
                          ? "bg-white text-zinc-900 shadow-sm"
                          : "text-zinc-600 hover:text-zinc-800"
                      }`}
                      data-testid="stream-visibility-private"
                    >
                      🔒 Private show
                    </button>
                  </div>
                  {streamForm.visibility === "private" ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 leading-snug" data-testid="private-show-note">
                      <b>Private = unlisted.</b> The show is hidden from the Live
                      grid, the Following feed, and category alerts. Only people
                      with the direct stream link can join. Bookmarkers still get
                      the "you're live" notification.
                    </div>
                  ) : (
                    <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-600 leading-snug" data-testid="public-show-note">
                      Public = listed everywhere — Live grid, Following feed,
                      and category alerts to opted-in fans.
                    </div>
                  )}
                  <Input
                    placeholder="Stream title"
                    value={streamForm.title}
                    onChange={(e) => setStreamForm({ ...streamForm, title: e.target.value })}
                    data-testid="stream-title-input"
                  />
                  <Input
                    placeholder="Short description (optional)"
                    value={streamForm.description}
                    onChange={(e) => setStreamForm({ ...streamForm, description: e.target.value })}
                  />
                  <Select
                    value={streamForm.category}
                    onValueChange={(v) => setStreamForm({ ...streamForm, category: v })}
                  >
                    <SelectTrigger data-testid="stream-category-select">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* 18+ age-restriction toggle — Whatnot-style. When ON
                      the stream gets an "18+" badge in the Live grid and
                      shows a confirmation modal before viewers can enter. */}
                  <label
                    className={`flex items-start gap-3 rounded-xl border px-3 py-3 cursor-pointer transition ${
                      streamForm.age_restricted
                        ? "border-rose-300 bg-rose-50"
                        : "border-zinc-200 bg-white hover:border-zinc-300"
                    }`}
                    data-testid="stream-age-restricted-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={!!streamForm.age_restricted}
                      onChange={(e) =>
                        setStreamForm({ ...streamForm, age_restricted: e.target.checked })
                      }
                      className="mt-0.5 h-4 w-4 accent-rose-600"
                      data-testid="stream-age-restricted-input"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-display font-bold text-sm">
                          18+ only
                        </span>
                        {streamForm.age_restricted && (
                          <span className="text-[10px] font-black tracking-widest bg-rose-600 text-white rounded px-1.5 py-0.5">
                            18+
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-zinc-600 leading-snug mt-0.5">
                        Mark this stream as for adults only. Viewers must confirm
                        they're 18+ before entry. Use for content with mature
                        themes, alcohol, or similar age-gated categories.
                      </p>
                    </div>
                  </label>

                  <div>
                    <label className="overline text-zinc-500 block mb-1">
                      SCHEDULE FOR LATER (OPTIONAL)
                    </label>
                    <Input
                      type="datetime-local"
                      value={streamForm.scheduled_at}
                      onChange={(e) =>
                        setStreamForm({ ...streamForm, scheduled_at: e.target.value })
                      }
                      data-testid="stream-schedule-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Leave empty to go live now. Scheduled shows can be bookmarked by fans.
                    </p>
                  </div>
                  <Button
                    onClick={startStream}
                    disabled={creatingStream || !streamForm.title}
                    className="w-full bg-[#1D78AB] hover:bg-[#165d85] text-white gap-2"
                    data-testid="go-live-btn"
                  >
                    <BroadcastIcon size={16} />
                    {streamForm.scheduled_at ? "Schedule show" : "Go live now"}
                  </Button>
                </div>
              </div>
            </TabsContent>
          )}

          {isSeller && (
            <TabsContent value="posts" className="mt-6">
              <SellerPostsTab user={user} />
            </TabsContent>
          )}

          {isSeller && (
            <TabsContent value="highlights" className="mt-6">
              <FrenzyHighlightsTab />
            </TabsContent>
          )}

          <TabsContent value="bids" className="mt-6">
            {myBids.length === 0 ? (
              <Empty title="No bids yet" cta="Browse auctions" to="/browse?type=auction" />
            ) : (
              <div className="border border-zinc-200 bg-white divide-y divide-zinc-200">
                {myBids.map((b) => (
                  <Link
                    key={b.id}
                    to={`/listing/${b.listing_id}`}
                    className="p-4 flex items-center justify-between hover:bg-zinc-50"
                    data-testid={`my-bid-${b.id}`}
                  >
                    <div>
                      <div className="font-semibold">Listing {b.listing_id.slice(-8)}</div>
                      <div className="text-xs text-zinc-500">
                        {new Date(b.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="font-mono font-bold text-[#1D78AB]">
                      ${b.amount.toFixed(2)}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            {myOrders.length === 0 ? (
              <Empty title="No orders yet" cta="Browse marketplace" to="/browse" />
            ) : (
              <div className="border border-zinc-200 bg-white divide-y divide-zinc-200">
                {myOrders.map((o) => (
                  <div key={o.session_id} className="p-4 flex items-center gap-3" data-testid={`my-order-${o.session_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{o.listing?.title || "Listing"}</div>
                      <div className="text-xs text-zinc-500">
                        {new Date(o.created_at).toLocaleString()} · {o.payment_status}
                        {o.is_digital && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#1D78AB]/10 text-[#1D78AB] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                            Digital · Ready
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="font-mono font-bold text-[#1D78AB] hidden sm:block">
                      ${Number(o.amount).toFixed(2)}
                    </div>
                    {o.is_digital && o.order_id && (
                      <DigitalDownloadButton
                        orderId={o.order_id}
                        filename={o.listing?.digital_filename}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="library" className="mt-6">
            <DigitalLibraryTab />
          </TabsContent>
        </Tabs>
      </main>
      <MessageTopBuyersModal open={topBuyersOpen} onOpenChange={setTopBuyersOpen} />
      <Footer />
    </div>
  );
}

function DigitalLibraryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/downloads");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(apiError(e) || "Could not load library");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // After a successful download we re-fetch so the count + last-opened
  // timestamps reflect the click immediately (no full page refresh).
  const onDownloaded = () => load();

  // Compute per-seller engagement so the UI can surface a single VIP
  // collector badge (the seller this buyer engages with most) plus a
  // softer "Frequent" badge for any seller they've downloaded ≥3 times.
  // Tie-break: total downloads → item count → first-seen seller wins.
  const sellerStats = React.useMemo(() => {
    const map = new Map();
    items.forEach((it, idx) => {
      const sid = it.seller_id;
      if (!sid) return;
      const cur = map.get(sid) || {
        seller_id: sid,
        seller_name: it.seller_name,
        items: 0,
        downloads: 0,
        first_idx: idx,
      };
      cur.items += 1;
      cur.downloads += Number(it.download_count || 0);
      map.set(sid, cur);
    });
    let top = null;
    map.forEach((s) => {
      if (
        !top ||
        s.downloads > top.downloads ||
        (s.downloads === top.downloads && s.items > top.items) ||
        (s.downloads === top.downloads &&
          s.items === top.items &&
          s.first_idx < top.first_idx)
      ) {
        top = s;
      }
    });
    // Only crown a VIP when there's actual engagement (≥1 download)
    // OR ≥3 owned items from the same seller — avoids slapping the
    // badge on someone after a single never-opened purchase.
    if (top && top.downloads === 0 && top.items < 3) top = null;
    return { byId: map, topSellerId: top ? top.seller_id : null };
  }, [items]);

  if (loading) {
    return (
      <div className="text-center text-sm text-zinc-500 py-10" data-testid="library-loading">
        Loading your library…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Empty
        title="Your digital library is empty"
        cta="Find a digital drop"
        to="/browse"
      />
    );
  }

  return (
    <div data-testid="digital-library">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-display font-bold text-xl">Digital library</p>
          <p className="text-xs text-zinc-500 mt-0.5">
            Every downloadable you own — re-open any file, any time.
          </p>
        </div>
        <span className="text-[11px] font-mono tracking-wider text-zinc-400">
          {items.length} {items.length === 1 ? "item" : "items"}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <LibraryCard
            key={it.order_id}
            item={it}
            onDownloaded={onDownloaded}
            sellerStats={sellerStats}
          />
        ))}
      </div>
    </div>
  );
}

function LibraryCard({ item, onDownloaded, sellerStats }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const lastOpened = item.last_downloaded_at
    ? new Date(item.last_downloaded_at).toLocaleString()
    : null;
  const thumbUrl = item.thumbnail ? `${SITE_ORIGIN}/api/files/${item.thumbnail}` : null;

  const sellerInfo = sellerStats?.byId?.get(item.seller_id);
  const isVip = sellerStats?.topSellerId && item.seller_id === sellerStats.topSellerId;
  const isFrequent = !isVip && sellerInfo && (sellerInfo.downloads >= 3 || sellerInfo.items >= 3);

  return (
    <div
      className="border border-zinc-200 rounded-2xl bg-white overflow-hidden flex flex-col"
      data-testid={`library-item-${item.order_id}`}
    >
      <Link
        to={`/listing/${item.listing_id}`}
        className="aspect-video bg-zinc-100 flex items-center justify-center overflow-hidden"
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt={item.title} className="h-full w-full object-cover" />
        ) : (
          <DownloadSimpleIcon size={42} className="text-zinc-300" />
        )}
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="inline-flex items-center rounded-full bg-[#1D78AB]/10 text-[#1D78AB] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
            Digital
          </span>
          {item.category && (
            <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">
              {item.category}
            </span>
          )}
          {isVip && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
              data-testid={`library-vip-badge-${item.order_id}`}
              title="You're this creator's top collector"
            >
              <CrownSimpleIcon size={11} weight="fill" />
              VIP Collector
            </span>
          )}
          {isFrequent && (
            <span
              className="inline-flex items-center rounded-full bg-zinc-100 text-zinc-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
              data-testid={`library-frequent-badge-${item.order_id}`}
              title="You collect from this creator regularly"
            >
              Frequent
            </span>
          )}
        </div>
        <p className="font-display font-bold text-base leading-tight line-clamp-2">
          {item.title || "Digital item"}
        </p>
        {item.seller_name && (
          <p className="text-xs text-zinc-500 mt-1">by {item.seller_name}</p>
        )}
        <p className="text-[11px] text-zinc-400 font-mono mt-2 truncate" title={item.filename || ""}>
          {item.filename || "—"}
        </p>
        <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
          Purchased {new Date(item.purchased_at).toLocaleDateString()}
          {lastOpened && (
            <>
              <br />
              Last opened {lastOpened} · {item.download_count}×
            </>
          )}
          {!lastOpened && (
            <>
              <br />
              <span className="text-emerald-600 font-semibold">Never opened</span>
            </>
          )}
        </div>
        <div className="mt-auto pt-4 flex items-center gap-2 flex-wrap">
          <DigitalDownloadButton
            orderId={item.order_id}
            filename={item.filename}
            onDownloaded={onDownloaded}
          />
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-xs font-bold px-3 py-2"
            data-testid={`library-share-${item.order_id}`}
            aria-label="Share to followers"
          >
            <ShareNetworkIcon size={13} weight="bold" />
            Share
          </button>
          {item.seller_id && (
            <button
              type="button"
              onClick={() => setTipOpen(true)}
              className={`inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-3 py-2 transition-colors ${
                isVip
                  ? "bg-amber-500 hover:bg-amber-600 text-white"
                  : "border border-rose-300 hover:bg-rose-50 text-rose-600"
              }`}
              data-testid={`library-tip-${item.order_id}`}
              aria-label={`Tip ${item.seller_name || "this creator"}`}
            >
              <HandHeartIcon size={13} weight="fill" />
              Tip
            </button>
          )}
        </div>
      </div>
      <ShareLibraryItemModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        item={item}
      />
      <TipSellerModal
        open={tipOpen}
        onOpenChange={setTipOpen}
        sellerId={item.seller_id}
        sellerName={item.seller_name}
        isVip={isVip}
      />
    </div>
  );
}

function TipSellerModal({ open, onOpenChange, sellerId, sellerName, isVip }) {
  const PRESETS = [1, 5, 10, 25];
  const [amount, setAmount] = useState(5);
  const [custom, setCustom] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open) {
      setAmount(5);
      setCustom("");
      setMessage("");
    }
  }, [open]);

  const effective = custom ? Number(custom) : amount;
  const valid = effective >= 1 && effective <= 1000;

  const onTip = async () => {
    if (!sellerId || !valid) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/sellers/${sellerId}/tip/checkout`, {
        amount_usd: Number(effective),
        message: message.slice(0, 200),
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start tip checkout");
        setLoading(false);
      }
    } catch (e) {
      toast.error(apiError(e) || "Tip failed");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="tip-seller-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <HandHeartIcon size={20} weight="fill" className="text-rose-500" />
            Tip {sellerName || "the creator"}
          </DialogTitle>
          <DialogDescription>
            100% goes to the seller. No platform cut — just thanks.
            {isVip && (
              <span className="block mt-1 text-amber-700 font-semibold">
                You're their top collector. Make it count ✨
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-2 mt-2">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setAmount(p);
                setCustom("");
              }}
              className={`rounded-xl px-3 py-3 text-sm font-bold border transition-colors ${
                !custom && amount === p
                  ? "bg-[#1D78AB] text-white border-[#1D78AB]"
                  : "bg-white text-zinc-800 border-zinc-200 hover:border-zinc-400"
              }`}
              data-testid={`tip-preset-${p}`}
            >
              ${p}
            </button>
          ))}
        </div>
        <div className="mt-3">
          <label className="text-[11px] uppercase tracking-wider font-bold text-zinc-500">
            Or custom amount (USD)
          </label>
          <Input
            type="number"
            min="1"
            max="1000"
            step="1"
            inputMode="decimal"
            placeholder="e.g. 7"
            value={custom}
            onChange={(e) => setCustom(e.target.value.replace(/[^0-9.]/g, ""))}
            className="mt-1"
            data-testid="tip-custom-input"
          />
        </div>
        <div className="mt-3">
          <label className="text-[11px] uppercase tracking-wider font-bold text-zinc-500">
            Note (optional)
          </label>
          <Textarea
            placeholder={`Loved your last drop…`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={200}
            rows={2}
            className="mt-1"
            data-testid="tip-message-input"
          />
          <p className="text-[10px] text-zinc-400 mt-1 text-right">{message.length}/200</p>
        </div>
        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            data-testid="tip-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onTip}
            disabled={!valid || loading || !sellerId}
            className="rounded-full bg-rose-500 hover:bg-rose-600 disabled:opacity-50"
            data-testid="tip-confirm"
          >
            {loading ? "Redirecting…" : `Tip $${valid ? Number(effective).toFixed(2) : "—"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareLibraryItemModal({ open, onOpenChange, item }) {
  const { user } = useAuth();
  const defaultText = `Just unlocked: ${item.title || "a Bidorah drop"} 🔓`;
  const [text, setText] = useState(defaultText);
  const [posting, setPosting] = useState(false);
  // Use the server-rendered share URL so when the link is pasted into
  // iMessage / WhatsApp / Discord / Slack / X the bot crawler fetches
  // proper OG tags (title + thumbnail + price) and humans get bounced
  // straight to the SPA listing page.
  const listingUrl = `${PUBLIC_HOST}/api/share/listing/${item.listing_id}`;

  // Reset the textarea every time the modal opens so re-shares don't
  // accidentally carry a previous edit forward.
  useEffect(() => {
    if (open) setText(defaultText);
  }, [open, defaultText]);

  const postToFollowers = async () => {
    setPosting(true);
    try {
      await api.post("/me/share-purchase", {
        order_id: item.order_id,
        text,
      });
      toast.success("Shared to your followers");
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e) || "Could not share");
    } finally {
      setPosting(false);
    }
  };

  const shareExternally = async () => {
    const payload = {
      title: item.title || "Bidorah drop",
      text,
      url: listingUrl,
    };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(payload);
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(`${text} ${listingUrl}`);
        toast.success("Link copied to clipboard");
      } else {
        toast.info(listingUrl);
      }
    } catch {
      /* user canceled native sheet — silent */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="share-library-dialog">
        <DialogHeader>
          <DialogTitle>Share to followers</DialogTitle>
          <DialogDescription>
            Post this drop to your Bidorah feed so everyone who follows you sees it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={1500}
            rows={4}
            placeholder="Say something about this drop…"
            data-testid="share-library-text"
          />
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-500 truncate">
            {listingUrl}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={shareExternally}
            data-testid="share-library-external"
          >
            Share elsewhere…
          </Button>
          <Button
            type="button"
            onClick={postToFollowers}
            disabled={posting || !user}
            className="bg-[#1D78AB] hover:bg-[#176089] text-white"
            data-testid="share-library-post"
          >
            {posting ? "Posting…" : "Post to followers"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DigitalDownloadButton({ orderId, filename, onDownloaded }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const isNative =
        typeof window !== "undefined" &&
        window.Capacitor &&
        window.Capacitor.isNativePlatform &&
        window.Capacitor.isNativePlatform();

      // ============================================================
      // NATIVE (Capacitor APK) — preferred path:
      //   We delegate the download to the OS download manager via the
      //   Capacitor Browser plugin. `Browser.open({ url })` launches
      //   Chrome Custom Tabs on Android (SFSafariViewController on
      //   iOS), and the OS picks up the `Content-Disposition:
      //   attachment` header from our signed URL and saves the file
      //   into the real, user-visible Downloads folder — with the
      //   familiar system download notification.
      //
      //   Why not Filesystem.writeFile? Because Capacitor's
      //   `Directory.Documents` lands the file in the app's private
      //   Documents path on Android, NOT in the device's Downloads
      //   folder users actually open. Browser.open writes to the
      //   correct user-facing location every time.
      //
      //   Fallbacks (in order):
      //     2) `window.open(url, "_system")` for Capacitor shells
      //        that don't bundle the Browser plugin yet.
      //     3) `window.location.href = url` as the last resort —
      //        rarely useful (Android WebView ignores download
      //        headers without setDownloadListener) but harmless.
      // ============================================================
      if (isNative) {
        try {
          // (0) Mint the signed URL (the OS browser is anon — token IS
          //     the auth) on the backend.
          const { data } = await api.post(`/me/downloads/${orderId}/signed-url`);
          const path = data?.url;
          if (!path) throw new Error("No signed URL returned");
          // SITE_ORIGIN is the build-time-injected backend URL.
          const backend = String(SITE_ORIGIN || window.location.origin).replace(/\/+$/, "");
          const absUrl = `${backend}${path}`;

          // (1) Capacitor Browser plugin → OS download manager.
          let openedViaBrowser = false;
          try {
            const mod = await import("@capacitor/browser");
            const Browser = mod?.Browser;
            if (Browser && typeof Browser.open === "function") {
              await Browser.open({ url: absUrl, presentationStyle: "popover" });
              openedViaBrowser = true;
            }
          } catch (brErr) {
            console.warn("Browser plugin unavailable, falling back:", brErr?.message || brErr);
          }

          // (2) Legacy Cordova-style system browser hand-off.
          if (!openedViaBrowser) {
            try {
              const w = window.open(absUrl, "_system");
              if (!w) {
                // (3) Last-resort same-WebView navigation.
                window.location.href = absUrl;
              }
            } catch (_) {
              window.location.href = absUrl;
            }
          }

          toast.success("Downloading…", {
            description: "Saving to your phone's Downloads folder.",
          });
        } catch (signErr) {
          console.error("Native download failed:", signErr);
          toast.error(apiError(signErr) || "Could not start the download");
          setBusy(false);
          return;
        }
      } else {
        // ============================================================
        // WEB / DESKTOP — direct blob download (classic browser flow).
        // ============================================================
        const resp = await api.get(`/me/downloads/${orderId}/file`, { responseType: "blob" });
        const blob = new Blob([resp.data], {
          type: resp.headers["content-type"] || "application/octet-stream",
        });
        const safeName = filename || `bidorah-download-${orderId}`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = safeName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
      if (typeof onDownloaded === "function") onDownloaded();
    } catch (e) {
      toast.error(apiError(e) || e?.message || "Download failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-1.5 rounded-full bg-[#1D78AB] hover:bg-[#165d85] disabled:opacity-60 text-white text-xs font-bold px-3 py-2 flex-shrink-0"
      data-testid={`dashboard-download-${orderId}`}
    >
      <DownloadSimpleIcon size={13} weight="bold" />
      {busy ? "Downloading…" : "Download"}
    </button>
  );
}


function Empty({ title, cta, to }) {
  return (
    <div className="border border-zinc-200 p-12 text-center bg-white">
      <p className="font-display font-bold text-2xl">{title}</p>
      <Link to={to} className="inline-block mt-3 text-sm font-semibold text-[#1D78AB]">
        {cta} →
      </Link>
    </div>
  );
}

function SellerPostsTab({ user }) {
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [composeOpen, setComposeOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/sellers/${user.user_id}/posts?limit=40`);
      setPosts(res.data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [user.user_id]);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-display font-bold text-xl">Your posts</p>
          <p className="text-xs text-zinc-500 mt-0.5">
            Posts appear in your followers' feed and on your public profile.
          </p>
        </div>
        <Button
          onClick={() => setComposeOpen(true)}
          className="bg-[#1D78AB] hover:bg-[#176089] text-white"
          data-testid="dashboard-compose-post-btn"
        >
          + New post
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-sm text-zinc-500 py-10">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-2xl p-10 text-center bg-zinc-50">
          <p className="font-display font-bold text-lg">No posts yet</p>
          <p className="text-sm text-zinc-500 mt-1">
            Share a behind-the-scenes shot, hype a drop, or feature one of
            your listings — your followers will see it instantly.
          </p>
          <Button
            onClick={() => setComposeOpen(true)}
            className="mt-4 bg-[#1D78AB] hover:bg-[#176089] text-white"
            data-testid="dashboard-empty-compose-post-btn"
          >
            Create your first post
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              currentUserId={user.user_id}
              onDeleted={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))}
            />
          ))}
        </div>
      )}

      <ComposePostModal
        open={composeOpen}
        onOpenChange={setComposeOpen}
        onPosted={(p) => setPosts((prev) => [p, ...prev])}
      />
    </div>
  );
}


function FrenzyHighlightsTab() {
  const [moments, setMoments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/me/frenzy-moments?limit=30");
        if (!cancelled) setMoments(res.data.items || []);
      } catch (e) {
        if (!cancelled) toast.error(apiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const copyShare = async (id) => {
    const url = `${SITE_ORIGIN}/moment/${id}`;
    try {
      if (navigator.share) {
        await navigator.share({ url, title: "🔥 FRENZY · Bidorah" });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere");
    } catch (_e) {
      /* user cancelled */
    }
  };

  return (
    <div data-testid="frenzy-highlights-tab">
      <div className="mb-4">
        <p className="font-display font-bold text-xl">Frenzy highlights</p>
        <p className="text-xs text-zinc-500 mt-0.5">
          Auto-captured every time a lot hits HOT or FRENZY heat. Share
          the moment — drop the link on TikTok, Instagram, X. The page
          unfurls with the lot image, winner, and bid velocity.
        </p>
      </div>

      {loading ? (
        <div className="text-center text-sm text-zinc-500 py-10">Loading…</div>
      ) : moments.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-2xl p-10 text-center bg-zinc-50">
          <p className="font-display font-bold text-lg">No frenzy yet</p>
          <p className="text-sm text-zinc-500 mt-1">
            Run a live auction. When bidders pile in fast enough we'll
            auto-capture the moment here.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {moments.map((m) => (
            <div
              key={m.id}
              className="rounded-2xl border border-zinc-200 bg-white overflow-hidden hover-lift"
              data-testid={`frenzy-highlight-${m.id}`}
            >
              <Link to={`/moment/${m.id}`} className="block">
                <div className="aspect-[4/5] bg-zinc-900 relative">
                  {m.listing_image ? (
                    <img
                      src={`${PUBLIC_HOST}/api/files/${m.listing_image}`}
                      alt={m.listing_title || ""}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-rose-600 to-amber-400" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80" />
                  <div className="absolute top-3 left-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black tracking-widest ${m.peak_level === "frenzy" ? "bg-gradient-to-r from-red-600 via-rose-500 to-amber-400 text-white" : "bg-gradient-to-r from-orange-500 to-rose-500 text-white"}`}>
                      🔥 {(m.peak_level || "hot").toUpperCase()}
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <div className="text-sm font-black truncate">{m.listing_title || "Live auction"}</div>
                    <div className="text-[11px] text-zinc-300 mt-0.5">
                      {m.peak_bids_5s || 0} bids/5s · {m.total_bids || 0} total
                    </div>
                    {m.winning_amount != null && (
                      <div className="text-[11px] text-amber-300 font-bold mt-0.5">
                        Hammered ${Number(m.winning_amount).toFixed(0)}{m.winner_name ? ` · @${m.winner_name}` : ""}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
              <div className="p-3 flex items-center justify-between">
                <span className="text-[11px] text-zinc-500">
                  {m.started_at ? new Date(m.started_at).toLocaleDateString() : ""}
                </span>
                <button
                  onClick={() => copyShare(m.id)}
                  className="inline-flex items-center gap-1 rounded-full bg-[#1D78AB] hover:bg-[#176089] text-white text-xs font-bold px-3 py-1.5"
                  data-testid={`frenzy-highlight-share-${m.id}`}
                >
                  <ShareNetworkIcon size={12} weight="bold" /> Share
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
